/**
 * CountryConfirmDialog — asks visitors to confirm their country before
 * an event RSVP so we can slice signups geographically.
 *
 * - Remembers the choice in localStorage so we only ask once.
 * - `onConfirm(country)` gets the ISO-2 code and closes the dialog.
 */
import React, { useEffect, useState } from 'react';
import { Globe, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';

const COUNTRIES = [
  { code: 'US', label: 'United States' }, { code: 'CA', label: 'Canada' },
  { code: 'GB', label: 'United Kingdom' }, { code: 'AU', label: 'Australia' },
  { code: 'NZ', label: 'New Zealand' }, { code: 'IE', label: 'Ireland' },
  { code: 'DE', label: 'Germany' }, { code: 'FR', label: 'France' },
  { code: 'NL', label: 'Netherlands' }, { code: 'ES', label: 'Spain' },
  { code: 'IT', label: 'Italy' }, { code: 'SE', label: 'Sweden' },
  { code: 'NO', label: 'Norway' }, { code: 'DK', label: 'Denmark' },
  { code: 'FI', label: 'Finland' }, { code: 'PL', label: 'Poland' },
  { code: 'BR', label: 'Brazil' }, { code: 'MX', label: 'Mexico' },
  { code: 'AR', label: 'Argentina' }, { code: 'ZA', label: 'South Africa' },
  { code: 'JP', label: 'Japan' }, { code: 'KR', label: 'South Korea' },
  { code: 'SG', label: 'Singapore' }, { code: 'IN', label: 'India' },
  { code: 'AE', label: 'United Arab Emirates' }, { code: 'IL', label: 'Israel' },
  { code: 'XX', label: 'Other' },
];

const STORAGE_KEY = 'fda_user_country_v1';

export const getSavedCountry = () => {
  try { return window.localStorage.getItem(STORAGE_KEY); } catch (_) { return null; }
};

const CountryConfirmDialog = ({ open, onOpenChange, onConfirm, eventTitle }) => {
  const [country, setCountry] = useState(() => getSavedCountry() || 'US');

  useEffect(() => {
    if (open) setCountry(getSavedCountry() || 'US');
  }, [open]);

  const confirm = () => {
    try { window.localStorage.setItem(STORAGE_KEY, country); } catch (_) { /* ignore */ }
    onConfirm && onConfirm(country);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="country-confirm-dialog">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-xl bg-primary/15 text-primary flex items-center justify-center flex-shrink-0">
              <Globe className="w-5 h-5" />
            </div>
            <div>
              <DialogTitle className="font-heading text-lg font-extrabold tracking-tight">
                Confirm your country of residence
              </DialogTitle>
              <DialogDescription>
                We use this to plan regional meet-ups and time-zone friendly webinars.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {eventTitle && (
          <div className="rounded-lg bg-muted/40 border border-border px-3 py-2 text-xs text-muted-foreground mt-2">
            You&apos;re signing up for: <span className="font-semibold text-foreground">{eventTitle}</span>
          </div>
        )}

        <div className="mt-3 space-y-2">
          <label className="text-xs font-semibold text-muted-foreground">Country</label>
          <Select value={country} onValueChange={setCountry}>
            <SelectTrigger data-testid="country-select-trigger">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="max-h-72">
              {COUNTRIES.map((c) => (
                <SelectItem key={c.code} value={c.code} data-testid={`country-option-${c.code}`}>
                  {c.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="mt-4 flex items-center justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)} className="rounded-full font-semibold text-muted-foreground">
            Cancel
          </Button>
          <Button onClick={confirm} className="rounded-full font-extrabold" data-testid="country-confirm-btn">
            Confirm &amp; continue <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CountryConfirmDialog;
